import React, { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { getStoreDisplayName, getStoreMatchValues, normalizeValue } from "../DiscountSummary";

const getStoreCode = (store) =>
	store?.site_code || store?.siteCode || store?.store_code || store?.storeCode || store?.code || "";

const splitPastedText = (text = "") =>
	text
		.split(/[\n\r\t,;|]+/)
		.map((part) => part.trim())
		.filter(Boolean);

const buildOptionList = (values = []) => [...new Set(values.filter(Boolean))].sort();

function StorePickerDialog({ open, onClose, onApply, storePool = [], selectedStores = [] }) {
	const [searchTerm, setSearchTerm] = useState("");
	const [zoneFilter, setZoneFilter] = useState([]);
	const [stateFilter, setStateFilter] = useState([]);
	const [cityFilter, setCityFilter] = useState([]);
	const [localSelected, setLocalSelected] = useState([]);
	const [pastedText, setPastedText] = useState("");
	const [unmatchedTokens, setUnmatchedTokens] = useState([]);

	const stores = useMemo(() => {
		const seen = new Set();
		const list = [];

		storePool.forEach((store) => {
			const name = getStoreDisplayName(store);
			const normalizedName = normalizeValue(name);
			if (!normalizedName || seen.has(normalizedName)) return;

			seen.add(normalizedName);
			list.push({
				name,
				normalizedName,
				code: String(getStoreCode(store) || "").trim(),
				zone: String(store?.zone || "").trim(),
				state: String(store?.state || "").trim(),
				city: String(store?.city || "").trim(),
				grade: String(store?.store_grade || "").trim(),
				matchValues: getStoreMatchValues(store),
			});
		});

		return list.sort((first, second) => first.name.localeCompare(second.name));
	}, [storePool]);

	useEffect(() => {
		if (!open) return;
		setLocalSelected(
			selectedStores
				.map((store) => (typeof store === "object" ? getStoreDisplayName(store) : String(store || "").trim()))
				.filter(Boolean)
		);
		setSearchTerm("");
		setZoneFilter([]);
		setStateFilter([]);
		setCityFilter([]);
		setPastedText("");
		setUnmatchedTokens([]);
	}, [open]);

	const zoneOptions = useMemo(() => buildOptionList(stores.map((store) => store.zone)), [stores]);
	const stateOptions = useMemo(
		() =>
			buildOptionList(
				stores.filter((store) => !zoneFilter.length || zoneFilter.includes(store.zone)).map((store) => store.state)
			),
		[stores, zoneFilter]
	);
	const cityOptions = useMemo(
		() =>
			buildOptionList(
				stores
					.filter((store) => !zoneFilter.length || zoneFilter.includes(store.zone))
					.filter((store) => !stateFilter.length || stateFilter.includes(store.state))
					.map((store) => store.city)
			),
		[stores, zoneFilter, stateFilter]
	);

	const filteredStores = useMemo(() => {
		const search = normalizeValue(searchTerm);

		return stores.filter((store) => {
			if (zoneFilter.length && !zoneFilter.includes(store.zone)) return false;
			if (stateFilter.length && !stateFilter.includes(store.state)) return false;
			if (cityFilter.length && !cityFilter.includes(store.city)) return false;
			if (!search) return true;

			return [store.name, store.code, store.city, store.state, store.zone, store.grade]
				.map(normalizeValue)
				.some((field) => field.includes(search));
		});
	}, [stores, searchTerm, zoneFilter, stateFilter, cityFilter]);

	const selectedSet = useMemo(() => new Set(localSelected.map(normalizeValue)), [localSelected]);

	const toggleStore = (storeName) => {
		const normalized = normalizeValue(storeName);
		setLocalSelected((previous) =>
			selectedSet.has(normalized)
				? previous.filter((name) => normalizeValue(name) !== normalized)
				: [...previous, storeName]
		);
	};

	const selectAllFiltered = () => {
		setLocalSelected((previous) => {
			const existing = new Set(previous.map(normalizeValue));
			const additions = filteredStores
				.filter((store) => !existing.has(store.normalizedName))
				.map((store) => store.name);
			return [...previous, ...additions];
		});
	};

	const removeAllFiltered = () => {
		const filteredSet = new Set(filteredStores.map((store) => store.normalizedName));
		setLocalSelected((previous) => previous.filter((name) => !filteredSet.has(normalizeValue(name))));
	};

	const handleMatchPastedStores = () => {
		const tokens = splitPastedText(pastedText);
		if (!tokens.length) {
			setUnmatchedTokens([]);
			return;
		}

		const matchedNames = [];
		const unmatched = [];

		tokens.forEach((token) => {
			const normalizedToken = normalizeValue(token);
			const match = stores.find(
				(store) => store.normalizedName === normalizedToken || store.matchValues.includes(normalizedToken)
			);
			if (match) matchedNames.push(match.name);
			else unmatched.push(token);
		});

		setLocalSelected((previous) => {
			const existing = new Set(previous.map(normalizeValue));
			const additions = matchedNames.filter((name) => {
				const normalized = normalizeValue(name);
				if (existing.has(normalized)) return false;
				existing.add(normalized);
				return true;
			});
			return [...previous, ...additions];
		});
		setUnmatchedTokens([...new Set(unmatched)]);
	};

	const areAllFilteredSelected =
		filteredStores.length > 0 && filteredStores.every((store) => selectedSet.has(store.normalizedName));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle sx={{ fontSize: "16px", fontWeight: 600, pb: 1 }}>
				Select Stores
				<Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}>
					Search or filter, tick multiple stores at once, or paste a store list copied from Excel.
				</Typography>
			</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={1.5}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						<TextField
							size="small"
							placeholder="Search store, code, city, state"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
							sx={{ minWidth: 240, flex: 1 }}
						/>
						<Autocomplete
							multiple
							size="small"
							disableCloseOnSelect
							limitTags={1}
							options={zoneOptions}
							value={zoneFilter}
							onChange={(event, newValue) => setZoneFilter(newValue)}
							renderInput={(params) => <TextField {...params} placeholder="Zone" />}
							sx={{ minWidth: 150 }}
						/>
						<Autocomplete
							multiple
							size="small"
							disableCloseOnSelect
							limitTags={1}
							options={stateOptions}
							value={stateFilter}
							onChange={(event, newValue) => setStateFilter(newValue)}
							renderInput={(params) => <TextField {...params} placeholder="State" />}
							sx={{ minWidth: 150 }}
						/>
						<Autocomplete
							multiple
							size="small"
							disableCloseOnSelect
							limitTags={1}
							options={cityOptions}
							value={cityFilter}
							onChange={(event, newValue) => setCityFilter(newValue)}
							renderInput={(params) => <TextField {...params} placeholder="City" />}
							sx={{ minWidth: 150 }}
						/>
					</Stack>

					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
						<Button
							size="small"
							variant="contained"
							onClick={selectAllFiltered}
							disabled={!filteredStores.length || areAllFilteredSelected}
							sx={{ textTransform: "none", fontSize: "11px" }}
						>
							Select all shown ({filteredStores.length})
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={removeAllFiltered}
							disabled={!filteredStores.length}
							sx={{ textTransform: "none", fontSize: "11px" }}
						>
							Unselect shown
						</Button>
						<Button
							size="small"
							color="error"
							variant="text"
							onClick={() => setLocalSelected([])}
							disabled={!localSelected.length}
							sx={{ textTransform: "none", fontSize: "11px" }}
						>
							Clear all
						</Button>
						<Box flex={1} />
						<Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#101828" }}>
							{localSelected.length} selected
						</Typography>
					</Stack>

					<Box
						sx={{
							maxHeight: 300,
							overflowY: "auto",
							border: "1px solid #e4e7ec",
							borderRadius: "8px",
						}}
					>
						{filteredStores.length === 0 ? (
							<Typography sx={{ p: 2, fontSize: "12px", color: "#667085" }}>
								No store matches the current search or filters.
							</Typography>
						) : (
							filteredStores.map((store) => (
								<Box
									key={store.normalizedName}
									onClick={() => toggleStore(store.name)}
									sx={{
										display: "flex",
										alignItems: "center",
										px: 1,
										py: 0.25,
										cursor: "pointer",
										borderBottom: "1px solid #f2f4f7",
										"&:hover": { background: "#f8fafc" },
									}}
								>
									<Checkbox
										size="small"
										disableRipple
										checked={selectedSet.has(store.normalizedName)}
										sx={{ p: "2px", mr: 1 }}
									/>
									<Box>
										<Typography sx={{ fontSize: "12px", color: "#101828" }}>{store.name}</Typography>
										<Typography sx={{ fontSize: "10px", color: "#667085" }}>
											{[store.code, store.city, store.state, store.zone, store.grade]
												.filter(Boolean)
												.join(" • ")}
										</Typography>
									</Box>
								</Box>
							))
						)}
					</Box>

					<Divider />

					<Box>
						<Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#101828", mb: 0.5 }}>
							Paste store list
						</Typography>
						<Typography sx={{ fontSize: "10px", color: "#667085", mb: 1 }}>
							Paste store names or store codes (one per line, or separated by comma/tab) and they will be
							ticked automatically.
						</Typography>
						<TextField
							multiline
							minRows={3}
							fullWidth
							size="small"
							placeholder={"Store A\nStore B\n1234"}
							value={pastedText}
							onChange={(event) => setPastedText(event.target.value)}
						/>
						<Stack direction="row" spacing={1} alignItems="center" mt={1} flexWrap="wrap" useFlexGap>
							<Button
								size="small"
								variant="outlined"
								onClick={handleMatchPastedStores}
								disabled={!pastedText.trim()}
								sx={{ textTransform: "none", fontSize: "11px" }}
							>
								Match &amp; select
							</Button>
							{unmatchedTokens.length > 0 && (
								<Typography sx={{ fontSize: "11px", color: "#b42318" }}>
									Not found in store master:
								</Typography>
							)}
							{unmatchedTokens.slice(0, 8).map((token) => (
								<Chip key={token} label={token} size="small" color="error" variant="outlined" />
							))}
							{unmatchedTokens.length > 8 && (
								<Typography sx={{ fontSize: "11px", color: "#b42318" }}>
									+{unmatchedTokens.length - 8} more
								</Typography>
							)}
						</Stack>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} sx={{ textTransform: "none", fontSize: "12px" }}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={() => onApply(localSelected)}
					sx={{ textTransform: "none", fontSize: "12px" }}
				>
					Apply {localSelected.length ? `(${localSelected.length})` : ""}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default StorePickerDialog;
